import React, { useState, useEffect } from 'react';
import { adminAPI } from '@/contexts/AdminAuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Settings, 
  Save, 
  RotateCcw, 
  AlertTriangle, 
  CheckCircle, 
  Wrench,
  Shield,
  Users,
  Mail,
  Database,
  Loader2
} from 'lucide-react';

interface Setting {
  _id: string;
  key: string;
  value: any;
  description: string;
  category: string;
  isPublic: boolean;
  lastModifiedBy?: {
    adminEmail: string;
    modifiedAt: string;
  };
}

const AdminSettingsPage: React.FC = () => {
  const [settings, setSettings] = useState<Setting[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editedValues, setEditedValues] = useState<Record<string, any>>({});

  // Maintenance mode state
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceReason, setMaintenanceReason] = useState('');

  // User limits state
  const [userLimits, setUserLimits] = useState({
    daily_leads_free: 100,
    daily_leads_premium: 1000,
    daily_emails_free: 50,
    daily_emails_premium: 500,
    max_files_free: 10,
    max_files_premium: 100
  });

  // Counter reset state
  const [resetCounters, setResetCounters] = useState({
    counterType: 'all',
    userIds: ''
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await adminAPI.getSettings();
      setSettings(response.data.settings);
      
      // Extract current values
      const values: Record<string, any> = {};
      response.data.settings.forEach((setting: Setting) => {
        values[setting.key] = setting.value;
      });
      setEditedValues(values);
      
      // Set maintenance mode
      setMaintenanceMode(values['system.maintenance_mode'] || false);
      
      // Set user limits
      setUserLimits({
        daily_leads_free: values['limits.daily_leads_free'] || 100,
        daily_leads_premium: values['limits.daily_leads_premium'] || 1000,
        daily_emails_free: values['limits.daily_emails_free'] || 50,
        daily_emails_premium: values['limits.daily_emails_premium'] || 500,
        max_files_free: values['limits.max_files_free'] || 10,
        max_files_premium: values['limits.max_files_premium'] || 100
      });
    } catch (error: any) {
      setError(error.message || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSettingChange = (key: string, value: any) => {
    setEditedValues(prev => ({ ...prev, [key]: value }));
  };

  const saveSetting = async (key: string) => {
    try {
      setSaving(key);
      setError('');
      setSuccess('');
      
      await adminAPI.updateSetting(key, editedValues[key]);
      setSuccess(`Setting ${key} updated successfully`);
      
      // Reload settings to get updated data
      await loadSettings();
    } catch (error: any) {
      setError(error.message || `Failed to update ${key}`);
    } finally {
      setSaving(null);
    }
  };

  const toggleMaintenanceMode = async () => {
    try {
      setSaving('maintenance');
      setError('');
      setSuccess('');
      
      const newMode = !maintenanceMode;
      await adminAPI.toggleMaintenanceMode(newMode, maintenanceReason);
      
      setMaintenanceMode(newMode);
      setSuccess(`Maintenance mode ${newMode ? 'enabled' : 'disabled'}`);
      
      await loadSettings();
    } catch (error: any) {
      setError(error.message || 'Failed to toggle maintenance mode');
    } finally {
      setSaving(null);
    }
  };

  const updateUserLimits = async () => {
    try {
      setSaving('limits');
      setError('');
      setSuccess('');
      
      await adminAPI.updateUserLimits(userLimits);
      setSuccess('User limits updated successfully');
      
      await loadSettings();
    } catch (error: any) {
      setError(error.message || 'Failed to update user limits');
    } finally {
      setSaving(null);
    }
  };

  const resetUserCounters = async () => {
    try {
      setSaving('reset');
      setError('');
      setSuccess('');
      
      const userIds = resetCounters.userIds 
        ? resetCounters.userIds.split(',').map(id => id.trim()).filter(Boolean)
        : undefined;
      
      const response = await adminAPI.resetUserCounters(userIds, resetCounters.counterType);
      setSuccess(`Reset ${resetCounters.counterType} counters for ${response.data.affectedUsers} users`);
    } catch (error: any) {
      setError(error.message || 'Failed to reset counters');
    } finally {
      setSaving(null);
    }
  };

  const getSettingsByCategory = (category: string) => {
    return settings.filter(setting => setting.category === category);
  };

  const renderSettingInput = (setting: Setting) => {
    const value = editedValues[setting.key];
    
    if (typeof value === 'boolean') {
      return (
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={value}
            onChange={(e) => handleSettingChange(setting.key, e.target.checked)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-600">
            {value ? 'Enabled' : 'Disabled'}
          </span>
        </div>
      );
    }
    
    if (typeof value === 'number') {
      return (
        <Input
          type="number"
          value={value}
          onChange={(e) => handleSettingChange(setting.key, parseInt(e.target.value) || 0)}
          className="w-32"
        />
      );
    }
    
    return (
      <Input
        type="text"
        value={value || ''}
        onChange={(e) => handleSettingChange(setting.key, e.target.value)}
        className="w-64"
      />
    );
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">System Settings</h1>
        </div>
        <div className="grid grid-cols-1 gap-6">
          {[...Array(3)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Settings className="h-6 w-6" />
            System Settings
          </h1>
          <p className="text-muted-foreground mt-1">
            Configure system-wide settings and user limits
          </p>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <Alert className="bg-red-50 border-red-200">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="text-red-800">
            {error}
          </AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="bg-green-50 border-green-200">
          <CheckCircle className="h-4 w-4" />
          <AlertDescription className="text-green-800">
            {success}
          </AlertDescription>
        </Alert>
      )}

      {/* Maintenance Mode */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wrench className="h-5 w-5" />
            Maintenance Mode
          </CardTitle>
          <CardDescription>
            Enable maintenance mode to lock out all users from the system
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-card border border-border rounded-lg">
            <div>
              <div className="font-medium">
                Maintenance Mode: {maintenanceMode ? (
                  <Badge variant="destructive">Enabled</Badge>
                ) : (
                  <Badge variant="default" className="bg-green-100 text-green-800">Disabled</Badge>
                )}
              </div>
              <div className="text-sm text-muted-foreground mt-1">
                {maintenanceMode
                  ? 'All users are currently locked out of the system'
                  : 'System is operating normally'
                }
              </div>
            </div>
            <Button
              onClick={toggleMaintenanceMode}
              disabled={saving === 'maintenance'}
              variant={maintenanceMode ? 'destructive' : 'default'}
            >
              {saving === 'maintenance' ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              {maintenanceMode ? 'Disable' : 'Enable'}
            </Button>
          </div>
          
          <div>
            <label className="text-sm font-medium text-muted-foreground mb-1 block">
              Reason (optional)
            </label>
            <Input
              placeholder="Scheduled maintenance, system updates, etc."
              value={maintenanceReason}
              onChange={(e) => setMaintenanceReason(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {/* User Limits */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            User Limits
          </CardTitle>
          <CardDescription>
            Set daily limits for different user tiers
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="font-medium text-foreground">Free Tier Limits</h3>
              
              <div>
                <label className="text-sm font-medium text-muted-foreground mb-1 block">
                  Daily Leads
                </label>
                <Input
                  type="number"
                  value={userLimits.daily_leads_free}
                  onChange={(e) => setUserLimits(prev => ({ 
                    ...prev, 
                    daily_leads_free: parseInt(e.target.value) || 0 
                  }))}
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-muted-foreground mb-1 block">
                  Daily Emails
                </label>
                <Input
                  type="number"
                  value={userLimits.daily_emails_free}
                  onChange={(e) => setUserLimits(prev => ({ 
                    ...prev, 
                    daily_emails_free: parseInt(e.target.value) || 0 
                  }))}
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-muted-foreground mb-1 block">
                  Max Files
                </label>
                <Input
                  type="number"
                  value={userLimits.max_files_free}
                  onChange={(e) => setUserLimits(prev => ({ 
                    ...prev, 
                    max_files_free: parseInt(e.target.value) || 0 
                  }))}
                />
              </div>
            </div>
            
            <div className="space-y-4">
              <h3 className="font-medium text-foreground">Premium Tier Limits</h3>
              
              <div>
                <label className="text-sm font-medium text-muted-foreground mb-1 block">
                  Daily Leads
                </label>
                <Input
                  type="number"
                  value={userLimits.daily_leads_premium}
                  onChange={(e) => setUserLimits(prev => ({ 
                    ...prev, 
                    daily_leads_premium: parseInt(e.target.value) || 0 
                  }))}
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-muted-foreground mb-1 block">
                  Daily Emails
                </label>
                <Input
                  type="number"
                  value={userLimits.daily_emails_premium}
                  onChange={(e) => setUserLimits(prev => ({ 
                    ...prev, 
                    daily_emails_premium: parseInt(e.target.value) || 0 
                  }))}
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-muted-foreground mb-1 block">
                  Max Files
                </label>
                <Input
                  type="number"
                  value={userLimits.max_files_premium}
                  onChange={(e) => setUserLimits(prev => ({ 
                    ...prev, 
                    max_files_premium: parseInt(e.target.value) || 0 
                  }))}
                />
              </div>
            </div>
          </div>
          
          <Button
            onClick={updateUserLimits}
            disabled={saving === 'limits'}
            className="w-full md:w-auto"
          >
            {saving === 'limits' ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Update Limits
          </Button>
        </CardContent>
      </Card>

      {/* Reset Counters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RotateCcw className="h-5 w-5" />
            Reset User Counters
          </CardTitle>
          <CardDescription>
            Reset usage counters for users (leads, emails, files, chats)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground mb-1 block">
                Counter Type
              </label>
              <select
                value={resetCounters.counterType}
                onChange={(e) => setResetCounters(prev => ({ ...prev, counterType: e.target.value }))}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="all">All Counters</option>
                <option value="leads">Leads Only</option>
                <option value="emails">Emails Only</option>
                <option value="files">Files Only</option>
                <option value="chats">Chats Only</option>
              </select>
            </div>
            
            <div>
              <label className="text-sm font-medium text-muted-foreground mb-1 block">
                User IDs (optional)
              </label>
              <Input
                placeholder="uid1, uid2, uid3... (leave empty for all users)"
                value={resetCounters.userIds}
                onChange={(e) => setResetCounters(prev => ({ ...prev, userIds: e.target.value }))}
              />
            </div>
          </div>
          
          <Button
            onClick={resetUserCounters}
            disabled={saving === 'reset'}
            variant="destructive"
            className="w-full md:w-auto"
          >
            {saving === 'reset' ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <RotateCcw className="h-4 w-4 mr-2" />
            )}
            Reset Counters
          </Button>
        </CardContent>
      </Card>

      {/* System Settings */}
      {['system', 'email'].map(category => {
        const categorySettings = getSettingsByCategory(category);
        if (categorySettings.length === 0) return null;
        
        return (
          <Card key={category}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 capitalize">
                {category === 'system' && <Shield className="h-5 w-5" />}
                {category === 'email' && <Mail className="h-5 w-5" />}
                {category} Settings
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {categorySettings.map((setting) => (
                  <div key={setting.key} className="flex items-center justify-between p-4 bg-card border border-border rounded-lg">
                    <div className="flex-1">
                      <div className="font-medium text-foreground">{setting.key}</div>
                      <div className="text-sm text-muted-foreground mt-1">{setting.description}</div>
                      {setting.lastModifiedBy && (
                        <div className="text-xs text-muted-foreground mt-1">
                          Last modified by {setting.lastModifiedBy.adminEmail} on{' '}
                          {new Date(setting.lastModifiedBy.modifiedAt).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      {renderSettingInput(setting)}
                      <Button
                        size="sm"
                        onClick={() => saveSetting(setting.key)}
                        disabled={saving === setting.key}
                      >
                        {saving === setting.key ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Save className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default AdminSettingsPage;
